import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import SolutionCategory from "./SolutionCategory";
import { useSiteContent } from "../../services/content";
import { fig } from "../../theme";

import figLogo from "../../assets/logos/fig_CROP.png";
import swanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";
import swanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";

const categories = [
    {
        id: "flagship",
        title: "Our Flagship Products",
        description:
            "The two platforms at the heart of everything we do for K12.",
        bgColor: "#fff",
        products: [
            {
                name: "The FIG App — K12 LMS",
                logo: figLogo,
                description: (
                    <>
                        <strong>The learning & workforce development platform for K12 nutrition.</strong><br />
                        Online courses, CEU and certification tracking, community and messaging, and workforce development for your entire nutrition team — with compliance dashboards for every review or audit.
                        <br /><br />
                        Specifically built to scale: for individual districts, multi-district users, State Agencies, and Food Service Management companies — handling any sized workforce.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Creating an Environment of Consistency and Excellence across your district.</span>
                    </>
                ),
                link: "/contact",
                cta: "See The FIG App",
            },
            {
                name: "FIG Finance",
                logo: figLogo,
                description: (
                    <>
                        <strong>Real-time P&L and AI-augmented KPIs, built specifically for K12 nutrition.</strong><br />
                        FIG Finance is POS-, back-of-house-, and labor-agnostic — it connects to all of your systems and unifies them into a single, real-time picture of your department. AI-augmented KPIs give you the real financial story, with the latest reporting technology purpose-built for K12 nutrition.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Run your nutrition department as a business, not a cost center.</span>
                    </>
                ),
                link: "/contact",
                cta: "Get FIG Finance",
            },
        ],
    },
    {
        id: "nutrition",
        title: "Food & Nutrition",
        description:
            "Complete systems for school meal programs — biometric identification, product compliance, CEP forecasting, CACFP management, and special diets.",
        bgColor: "#eefbfb",
        products: [
            {
                name: "Hi5 Palm Scan",
                logo: figLogo,
                description: (
                    <>
                        <strong>Palm vein biometric identification for K12 cafeterias.</strong><br />
                        Fast, hygienic, and accurate student identification at the point of sale. Eliminates PINs, cards, and lines. Integrates with existing POS systems.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>The future of cafeteria checkout — no cards, no codes, just a wave of the hand.</span>
                    </>
                ),
                link: "/contact",
                cta: "See Hi5",
            },
            {
                name: "Product Analysis",
                logo: figLogo,
                description: (
                    <>
                        <strong>AI-powered product and ingredient analysis for K12 compliance.</strong><br />
                        Instantly analyze ingredient lists, nutrition labels, and product formulation statements. Verify Buy American Provision compliance, flag allergens, and build audit-ready documentation for every product you serve.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Every ingredient checked, every product documented — compliance without the paperwork.</span>
                    </>
                ),
                link: "/contact",
                cta: "See Product Analysis",
            },
            {
                name: "FIG KIDS",
                logo: figLogo,
                description: (
                    <>
                        <strong>CACFP daycare management made simple.</strong><br />
                        Child enrollment, attendance, meals, compliance, billing, and federal reimbursement claims — all in one platform built for CACFP providers.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>From enrollment to reimbursement, manage your entire CACFP program in one place.</span>
                    </>
                ),
                link: "/contact",
                cta: "See FIG KIDS",
            },
            {
                name: "AI CEP Grouping Calculator",
                logo: figLogo,
                description: (
                    <>
                        <strong>Maximize reimbursement with AI-optimized CEP grouping.</strong><br />
                        Model USDA Community Eligibility Provision scenarios in minutes. Forecast eligibility and reimbursement across your district, discover the optimal school groupings, and export audit-ready PDF and Excel reports.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Find the grouping that feeds more kids — and maximizes your federal reimbursement.</span>
                    </>
                ),
                link: "https://cep.figk-12.com",
                cta: "Try the CEP Calculator",
            },
            {
                name: "Special Diets",
                logo: figLogo,
                description: (
                    <>
                        <strong>Confidently manage allergies and dietary needs.</strong><br />
                        Real-time tools for staff, nurses, and parents. Track accommodations, generate reports, and ensure every child is served safely.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Safety and compliance for every meal, every student, every day.</span>
                    </>
                ),
                link: "/contact",
                cta: "Learn More",
            },
        ],
    },
    {
        id: "procurement",
        title: "Procurement, Audit & Grants",
        description:
            "AI-powered procurement analytics, automated audit tools, grant writing, and a marketplace for K12.",
        bgColor: "#fff",
        products: [
            {
                name: "Bid-FIG",
                logo: swanSolutionsLogo,
                description: (
                    <>
                        <strong>AI-powered bid and procurement analytics.</strong><br />
                        Bid analysis, award recommendations, and compliance verification for child nutrition RFPs. Unlock instant, explainable insights from your bid and invoice data.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Make better decisions faster with AI that understands K12 procurement.</span>
                    </>
                ),
                link: "/contact",
                cta: "See Bid-FIG",
            },
            {
                name: "Invoice Audit",
                logo: swanSolutionsLogo,
                description: (
                    <>
                        <strong>Catch errors before they become findings.</strong><br />
                        Automated invoice review and discrepancy detection. Pricing errors, contract mismatches, and duplicate charges — flagged instantly.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Protect your budget with automated, continuous audit coverage.</span>
                    </>
                ),
                link: "/contact",
                cta: "See Invoice Audit",
            },
            {
                name: "FIG Grants",
                logo: figLogo,
                description: (
                    <>
                        <strong>AI-powered grant writing for K12 schools.</strong><br />
                        Federal grant API integration, document management, collaborative workflows, and AI assistance to help your district win more funding.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Stop struggling with grant applications — let AI help you write winning proposals.</span>
                    </>
                ),
                link: "/contact",
                cta: "See FIG Grants",
            },
            {
                name: "FIG Marketplace",
                logo: figLogo,
                description: (
                    <>
                        <strong>Connect districts and vendors in one marketplace.</strong><br />
                        Streamlined digital ordering, product discovery, and vendor management for school nutrition programs.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>A better way to buy and sell in K12 nutrition.</span>
                    </>
                ),
                link: "/contact",
                cta: "See Marketplace",
            },
        ],
    },
    {
        id: "commercial",
        title: "Commercial Products",
        description:
            "Enterprise software available to any company — not just schools.",
        bgColor: "#eefbfb",
        products: [
            {
                name: "AIOS — Agentic Operating System",
                logo: swanAiLabsLogo,
                description: (
                    <>
                        <strong>AI infrastructure for any company.</strong><br />
                        AIOS federates your databases, coordinates AI agents, and builds an intelligence layer on top of your existing systems. Schema discovery, knowledge graphs, decision tracking, and continuous learning — out of the box.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Build AI-powered systems without starting from scratch.</span>
                    </>
                ),
                link: "https://aios.swancreate.com",
                cta: "Explore AIOS",
            },
            {
                name: "CRM",
                logo: swanSolutionsLogo,
                description: (
                    <>
                        <strong>Sales and relationship management for growing companies.</strong><br />
                        Full CRM with accounts, proposals, commissions, email marketing, pipeline tracking, and mobile PWA. Built for sales teams that need power without complexity.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>A CRM that works the way your sales team actually sells.</span>
                    </>
                ),
                link: "/contact",
                cta: "See CRM",
            },
        ],
    },
    {
        id: "ai",
        title: "Swan AI Labs & Custom Development",
        description:
            "AI services and custom software for any organization — K12 or otherwise.",
        bgColor: "#fff",
        products: [
            {
                name: "AI Services",
                logo: swanAiLabsLogo,
                description: (
                    <>
                        <strong>Turn AI research into real operational results.</strong><br />
                        RAG solutions, custom LLM chatbots, nutrient and product analysis, document AI, invoice automation, bid analytics, and deep visualization — purpose-built for your use case.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Not hype. Real AI that solves real problems.</span>
                    </>
                ),
                link: "/contact",
                cta: "Start Your AI Project",
            },
            {
                name: "Custom Integrations",
                logo: swanSolutionsLogo,
                description: (
                    <>
                        <strong>Make your systems work together — seamlessly.</strong><br />
                        Secure, scalable APIs and data pipelines connecting any systems your organization depends on. Unified reporting, automated workflows, and zero manual entry.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>We connect the systems you already have into something that actually works together.</span>
                    </>
                ),
                link: "/contact",
                cta: "See Integration Services",
            },
            {
                name: "Custom Software Development",
                logo: swanSolutionsLogo,
                description: (
                    <>
                        <strong>Custom software built to your spec.</strong><br />
                        Full-scale, enterprise-grade software for any industry. We design, build, and support platforms that fit your vision and your budget.
                        <br /><br />
                        <span style={{ color: fig.greenDark }}>Your in-house dev team without the overhead.</span>
                    </>
                ),
                link: "/contact",
                cta: "Start Your Project",
            },
        ],
    },
];

export default function Solutions() {
    const content = useSiteContent();
    return (
        <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
            {/* Hero */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 16 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: fig.gradientHero,
                    minHeight: { xs: "40vh", md: "36vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(21,154,162,0.18)",
                    mb: 0,
                }}
            >
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        mb: 2,
                        color: "#fff",
                        fontSize: { xs: "2.2rem", md: "3rem" },
                        letterSpacing: "-0.04em",
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    {content("solutions.hero.title", "Our Solutions")}
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        color: "rgba(255,255,255,0.95)",
                        fontWeight: 400,
                        maxWidth: 820,
                        mx: "auto",
                        fontSize: { xs: "1.1rem", md: "1.3rem" },
                        textShadow: "0 2px 14px rgba(20,92,140,0.13)",
                    }}
                >
                    {content("solutions.hero.subtitle", "EdTech platforms for K12 districts. Commercial software for any business. AI services and custom development for everyone.")}
                </Typography>
            </Box>

            {/* Category Sections */}
            {categories.map((cat) => (
                <SolutionCategory
                    key={cat.id}
                    id={cat.id}
                    title={cat.title}
                    description={cat.description}
                    products={cat.products}
                    bgColor={cat.bgColor}
                />
            ))}

            {/* Final CTA */}
            <Box sx={{ textAlign: "center", py: { xs: 6, md: 10 }, bgcolor: "#eefbfb" }}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            mb: 2,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        Your Vision. Our Technology.
                    </Typography>
                    <Typography
                        sx={{
                            maxWidth: 650,
                            mx: "auto",
                            color: "text.secondary",
                            fontSize: "1.13rem",
                            mb: 3,
                        }}
                    >
                        Swan Solutions is your partner for practical, reliable, and future-ready
                        software. Let's build something great together.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        href="/contact"
                        component={motion.a}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.96 }}
                        transition={{ type: "spring", stiffness: 330, damping: 16 }}
                        sx={{
                            borderRadius: 99,
                            fontWeight: 700,
                            fontSize: "1.15rem",
                            py: 1.5,
                            px: 5,
                            mt: 1,
                            background: fig.gradient,
                        }}
                    >
                        Start the Conversation
                    </Button>
                </motion.div>
            </Box>
        </Box>
    );
}
